import {
  useKineticMap,
  useStableResultData,
  MapContext,
  MapSearchResultItem,
  useMapBoxSearchContext,
  DEFAULT_ZOOM,
  useMapSearchBoxPin,
  MapSearchResultProps,
  useAppSearchHistory,
  publicUrl,
  AppSearchItemType,
  AppSearchIconKey,
  useGetNodes,
} from '@kinetic-ui/shared';
import { useConnectedVehiclesMapSearchQuery } from '@kinetic-ui/data-layer';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ENTITY_DRAWER_VIEW_ALL_ROUTE, ENTITY_EDIT_ROUTE } from '../EntityList/consts';
import { StatusLayerIds } from '../layers/const';
import ConnectedVehicleIcon from '@mui/icons-material/CellTower';
import useMapSearchResultStyles from './styles';

const LAYERS = [StatusLayerIds.RSU_MODE_STATUS_LAYER_ID, StatusLayerIds.RSU_MAIN_STATUS_LAYER_ID];

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const map = useKineticMap();
  const { searchText } = useMapBoxSearchContext();

  const result = useConnectedVehiclesMapSearchQuery({
    fetchPolicy: 'cache-and-network',
    variables: { searchText, limit: 5 },
  });
  const resultData = useStableResultData(result);
  const { selectedLayers, setSelectedLayers, hasEntityDrawer } = useContext(MapContext);
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();
  const classes = useMapSearchResultStyles();

  const connectedVehicles = useGetNodes(resultData?.connectedVehicles);
  const { push } = useHistory();
  const { addSearchHistory } = useAppSearchHistory();

  const onClick = useCallback(
    (id: string, coordinates: [number, number], openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const path = `/${ENTITY_EDIT_ROUTE}/${id}/`;
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        const connectedVehicle = connectedVehicles.find((e) => e.id === id);
        if (connectedVehicle?.name) {
          addSearchHistory({
            name: connectedVehicle.name,
            link: path,
            type: AppSearchItemType.View,
            icon: AppSearchIconKey.ConnectedVehicle,
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin({ type: 'Point', coordinates: coordinates ?? [0, 0] });
        return;
      }

      if (!selectedLayers.some((layerId) => LAYERS.includes(layerId as StatusLayerIds))) {
        setSelectedLayers([...selectedLayers, StatusLayerIds.RSU_MAIN_STATUS_LAYER_ID]);
      }

      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);
      map.setViewState({
        latitude: coordinates[1],
        longitude: coordinates[0],
        zoom: DEFAULT_ZOOM,
      });
    },
    [
      addMapPin,
      addSearchHistory,
      connectedVehicles,
      hasEntityDrawer,
      isAppSearch,
      map,
      onResultClick,
      push,
      selectedLayers,
      setSelectedLayers,
    ]
  );

  useEffect(() => {
    if (!hasEntityDrawer && searchText?.length > 0) {
      removeMapPin();
    }
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {connectedVehicles.map((connectedVehicle) => (
        <MapSearchResultItem
          key={`searchItem_${connectedVehicle.id}`}
          name={connectedVehicle.name}
          onClick={(openInNewWindow) =>
            onClick(connectedVehicle.id, connectedVehicle.location.coordinates, openInNewWindow)
          }
          icon={<ConnectedVehicleIcon className={classes.connectedVehicleIcon} />}
          newWindow={isAppSearch}
        />
      ))}
    </>
  );
};

export default MapSearchResult;
